import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Button from '../../../../components/ui/button';
import Input from '../../../../components/ui/input';
import { FiArrowLeft } from 'react-icons/fi';
import ExpenseTableClient from './components/ExpenseTableClient';
import CategoryBreakdownClient from './components/CategoryBreakdownClient';
import ExportButton from './components/ExportButton';
import MonthSelector from '../../admin/MonthSelector';
import { generateMonthOptions } from '../../shared/constants';
import SublyApi from '../../../../helpers/Api';

const ClientSpendingTracker = () => {
	const currentUser = useSelector((st) => st.user.currentUser);
	const username = currentUser?.username;
	const token = useSelector((st) => st.user.token);
	const [selectedMonth, setSelectedMonth] = useState(
		new Date().toISOString().slice(0, 7)
	);
	const [searchTerm, setSearchTerm] = useState('');
	const [expenses, setExpenses] = useState([]);
	const [summary, setSummary] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const monthOptions = generateMonthOptions(12);

	// Fetch expenses when month changes
	useEffect(() => {
		let cancelled = false;

		const fetchExpenses = async () => {
			if (!username || !token) {
				setLoading(false);
				return;
			}

			setLoading(true);
			setError(null);

			try {
				// Calculate month date range
				const year = selectedMonth.split('-')[0];
				const month = selectedMonth.split('-')[1];
				const lastDay = new Date(year, month, 0).getDate();

				const filters = {
					start_date: `${selectedMonth}-01`,
					end_date: `${selectedMonth}-${lastDay}`,
				};

				// Fetch expenses and summary for selected month
				const [expensesData, summaryData] = await Promise.all([
					SublyApi.getExpenses(filters),
					SublyApi.getExpenseSummary(filters)
				]);

				// If this effect was cleaned up while awaiting, discard results
				if (cancelled) return;

				// Normalize expense data: convert expenseDate → date and ensure amount is numeric
				// This fixes the "Invalid Date" bug and "$NaN" total bug
				const normalizedExpenses = (expensesData.expenses || expensesData).map(expense => ({
					...expense,
					date: expense.expenseDate || expense.date,
					amount: typeof expense.amount === 'string' ? parseFloat(expense.amount) : expense.amount
				}));

				setExpenses(normalizedExpenses);
				setSummary(summaryData);
			} catch (err) {
				if (cancelled) return;
				console.error('Error fetching expenses:', err);
				setError(err?.message || 'Failed to load expenses');
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		fetchExpenses();

		return () => {
			cancelled = true;
		};
	}, [selectedMonth, username, token]);

	// Filter expenses by search term
	const filteredExpenses = useMemo(() => {
		return expenses.filter((expense) => {
			if (searchTerm === '') return true;

			const matchesName = expense.name?.toLowerCase().includes(searchTerm.toLowerCase());
			const matchesNotes = expense.notes?.toLowerCase().includes(searchTerm.toLowerCase());

			return matchesName || matchesNotes;
		});
	}, [expenses, searchTerm]);

	const selectedMonthLabel =
		monthOptions.find((m) => m.value === selectedMonth)?.label ||
		selectedMonth;

	return (
		<div className='min-h-screen bg-gray-50' data-testid='spending-tracker'>
			<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
				{/* Back Button */}
				<div className='mb-6'>
					<Link to='/home' className='inline-flex items-center'>
						<Button variant='outline' size='sm' className='flex items-center gap-2'>
							<FiArrowLeft className='h-4 w-4' />
							Home
						</Button>
					</Link>
				</div>

				{/* Header */}
				<div className='mb-8'>
					<h1 className='text-4xl font-glacial font-bold text-clientBlack mb-2'>
						My Marketing Expenses
					</h1>
					<p className='text-gray-600 font-montserrat'>
						View your monthly marketing expenses and category breakdown
					</p>
				</div>

				{/* Error Message */}
				{error && (
					<div className='mb-6 bg-red-50 border border-red-200 rounded-lg p-4'>
						<p className='text-red-800 font-montserrat'>{error}</p>
					</div>
				)}

				{/* Controls */}
				<div className='mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between'>
					<div className='flex flex-col sm:flex-row gap-4 flex-1'>
						{/* Month Selector */}
						<div className='w-full sm:w-64'>
							<MonthSelector
								value={selectedMonth}
								onChange={setSelectedMonth}
								disabled={loading}
							/>
						</div>

						{/* Search */}
						<div className='w-full sm:w-80'>
							<Input
								type='text'
								placeholder='Search by name or notes...'
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								disabled={loading}
							/>
						</div>
					</div>

					{/* Export Button */}
					{filteredExpenses.length > 0 && !loading && (
						<ExportButton
							expenses={filteredExpenses}
							month={selectedMonthLabel}
							clientName={
								currentUser
									? `${currentUser.firstName} ${currentUser.lastName}`
									: 'Client'
							}
						/>
					)}
				</div>

				{/* Loading State */}
				{loading ? (
					<div className='bg-white rounded-lg border border-gray-200 p-12 text-center'>
						<p className='text-gray-500 font-montserrat'>Loading expenses...</p>
					</div>
				) : (
					<>
						{/* Category Breakdown */}
						{summary && summary.byCategory && summary.byCategory.length > 0 && (
							<div className='mb-6'>
								<CategoryBreakdownClient
									expenses={filteredExpenses}
									total={summary.totalAmount || 0}
									byCategory={summary.byCategory}
								/>
							</div>
						)}

						{/* Expense Table */}
						<ExpenseTableClient expenses={filteredExpenses} />

						{/* Empty State */}
						{filteredExpenses.length === 0 && !loading && (
							<div className='bg-white rounded-lg border border-gray-200 p-12 text-center'>
								<p className='text-gray-500 font-montserrat'>
									{searchTerm
										? 'No expenses match your search'
										: `No expenses found for ${selectedMonthLabel}`}
								</p>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default ClientSpendingTracker;
